import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import SidebarMenuItem from './SidebarMenuItem';
import styles from './AccordionMenuItem.module.css';

/**
 * AccordionMenuItem Component
 * Menu item with accordion functionality - can expand to show sub-items
 * Automatically opens when current route matches any sub-item
 */
const AccordionMenuItem = ({
  icon,
  label,
  path,
  subItems = [],
  isCollapsed = false,
  color,
  isVip = false,
  className = '',
  onItemClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasSubItems = subItems && subItems.length > 0;
  
  // Check if current route matches any sub-item or the main path
  const checkIsCurrentRoute = () => {
    const currentPath = location.pathname;
    // Check if current path matches the main path
    if (path && currentPath === path) {
      return true;
    }
    // Check if current path matches any sub-item path
    if (hasSubItems) {
      return subItems.some(subItem => {
        if (!subItem.path) return false;
        // Exact match
        if (currentPath === subItem.path) return true;
        // Check if current path starts with sub-item path (for nested routes)
        if (currentPath.startsWith(subItem.path + '/')) return true;
        return false;
      });
    }
    return false;
  };

  // Auto-open accordion if current route matches
  const [isOpen, setIsOpen] = useState(checkIsCurrentRoute());

  // Update open state when route changes
  useEffect(() => {
    const shouldBeOpen = checkIsCurrentRoute();
    if (shouldBeOpen) {
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, path, subItems]);

  if (isCollapsed) {
    // When collapsed, show as simple menu item
    return (
      <SidebarMenuItem
        icon={icon}
        label={label}
        path={path}
        isCollapsed={isCollapsed}
        color={color}
        isVip={isVip}
        className={className}
      />
    );
  }

  const handleChevronClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleLabelClick = (e) => {
    // When clicking the label, toggle the accordion to show sub-items
    if (hasSubItems) {
      setIsOpen(!isOpen);
    } else if (path) {
      // No sub-items → navigate directly
      if (onItemClick) {
        onItemClick(e);
      }
      navigate(path);
    }
  };

  const content = (
    <div 
      className={`${styles.accordionMenuItem} ${isOpen ? styles.open : ''} ${className}`}
    >
      <div 
        className={styles.accordionMenuItemContent}
        onClick={handleLabelClick}
        style={{ cursor: path ? 'pointer' : 'default' }}
      >
        <div className={styles.accordionIcon}>
          {icon}
        </div>
        <span className={styles.accordionLabel}>
          {label}
        </span>
      </div>
      {hasSubItems && (
        <div 
          className={styles.accordionChevron}
          onClick={handleChevronClick}
          style={{ cursor: 'pointer' }}
        >
          {isOpen ? (
            <ChevronDown size={16} />
          ) : (
            <ChevronRight size={16} />
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.accordionMenuItemWrapper}>
      {content}
      
      {isOpen && hasSubItems && (
        <div className={styles.accordionSubItems}>
          {subItems.map((subItem, index) => (
            <SidebarMenuItem
              key={subItem.path || index}
              icon={subItem.icon ? (typeof subItem.icon === 'string' ? <img src={subItem.icon} alt={subItem.name} loading="lazy" /> : subItem.icon) : undefined}
              label={subItem.name}
              path={subItem.path}
              isCollapsed={false}
              color="var(--color-text-secondary)"
              className={styles.subMenuItem}
              onClick={onItemClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AccordionMenuItem;
